function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseCommandResult<T>(content: unknown): T {
  if (!isRecord(content) || !isRecord(content['data'])) {
    throw new Error('Command returned an invalid response');
  }
  const attributes = content['data']['attributes'];
  if (!isRecord(attributes) || !('result' in attributes)) {
    throw new Error('Command did not return a result');
  }
  return attributes['result'] as T;
}

/** Why a command request failed, as far as the thrown request error says. */
export interface CommandFailure {
  /** The HTTP status; 0 when no response arrived (20 when aborted), undefined for a non-request error. */
  status?: number;
  /** The server's own reason: the envelope's `message`, else the first JSON:API error's detail or title. */
  message?: string;
}

/** Reads a thrown `store.request` error; its `content` is a CommandResponse envelope or a kit JSON:API `errors` document. */
export function readCommandFailure(error: unknown): CommandFailure {
  const failure: CommandFailure = {};
  if (!isRecord(error)) return failure;
  if (typeof error['status'] === 'number') failure.status = error['status'];
  const message = commandFailureMessage(error['content']);
  if (message) failure.message = message;
  return failure;
}

function commandFailureMessage(content: unknown): string | undefined {
  if (!isRecord(content)) return undefined;

  const data = content['data'];
  const attributes = isRecord(data) ? data['attributes'] : undefined;
  const envelopeMessage = isRecord(attributes) ? attributes['message'] : undefined;
  if (typeof envelopeMessage === 'string' && envelopeMessage) return envelopeMessage;

  const errors = Array.isArray(content['errors']) ? content['errors'] : [];
  const first = errors.find(isRecord);
  const detail = first?.['detail'] || first?.['title'];
  return typeof detail === 'string' && detail ? detail : undefined;
}

/** One refused field from a CommandResponse envelope's `commandErrors`. */
export interface CommandFieldError {
  /** JSON pointer into the request body, e.g. `/decisions/0/existing_loan_id`; null when the server named none. */
  pointer: string | null;
  detail: string;
}

/**
 * Reads the per-field refusals a thrown `store.request` error carries, in the
 * server's order. Entries without a `detail` are dropped.
 *
 * @returns an empty list for anything that is not a CommandResponse error envelope
 */
export function readCommandFieldErrors(error: unknown): CommandFieldError[] {
  if (!isRecord(error) || !isRecord(error['content'])) return [];
  const data = error['content']['data'];
  const attributes = isRecord(data) ? data['attributes'] : undefined;
  const errors = isRecord(attributes) ? attributes['commandErrors'] : undefined;
  if (!Array.isArray(errors)) return [];

  return errors.filter(isRecord).flatMap((entry) => {
    const detail = entry['detail'];
    if (typeof detail !== 'string' || !detail) return [];
    const source = entry['source'];
    const pointer = isRecord(source) ? source['pointer'] : undefined;
    return [{ pointer: typeof pointer === 'string' ? pointer : null, detail }];
  });
}
